import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../shared/Navbar';
import MarkdownContent from '../shared/MarkdownContent';
import { Course } from '../../types';
import { fetchCourses } from '../../services/api';
import { universityAreaBySlug, universitySections } from '../../utils/universityCatalogue';
import './LandingPage.css';
import './UniversityPage.css';
import { LearningNav, LearningFooter } from './LearningPageSections';

export default function UniversitySubjectPage() {
  const { subjectSlug = '' } = useParams();
  const area = universityAreaBySlug(subjectSlug);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = area ? `${area.name} | University | TeachMeNow` : 'University | TeachMeNow';
    return () => {
      document.title = previousTitle;
    };
  }, [area]);

  useEffect(() => {
    if (!area) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchCourses()
      .then(loaded => {
        if (!cancelled) setCourses(loaded);
      })
      .catch(() => {
        if (!cancelled) setError('The courses could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [area]);

  const sections = area ? universitySections(area, courses) : [];

  return (
    <div className="lp-root learning-page university-page">
      <Navbar />
      <LearningNav />
      <main>
        <section className="learning-catalogue-intro tm-shell">
          <Link className="university-back" to="/university">← All university subjects</Link>
          {!area ? (
            <>
              <h1>That subject is not listed</h1>
              <p>Choose Computer Science, Economics, Finance, and Management, Art, or English Literature.</p>
            </>
          ) : (
            <>
              <h1>{area.name}</h1>
              <p>{area.description}</p>
            </>
          )}
        </section>
        {area && (
          <section className="learning-section tm-shell" aria-label="Courses">
            {(loading || error || sections.length === 0) && (
              <p className="university-empty">
                {loading ? 'Loading courses…' : error || 'No university courses in this area yet.'}
              </p>
            )}
            {sections.map(section => (
              <div className="university-group" key={section.heading}>
                <h2>{section.heading}</h2>
                <div className="university-course-grid">
                  {section.courses.map(course => (
                    <Link className="browse-course-card" to={`/course/${course.id}`} key={`${section.heading}-${course.id}`}>
                      <div className="browse-course-card-top">
                        <div className="course-meta">
                          {course.subjects.map(subject => (
                            <span className="course-subject" key={subject}>{subject}</span>
                          ))}
                          {course.levels.map(level => (
                            <span className="course-level" key={level}>{level}</span>
                          ))}
                        </div>
                      </div>
                      <div className="browse-course-card-body">
                        <h3>{course.title}</h3>
                        <div className="browse-course-description course-markdown course-markdown--compact">
                          <MarkdownContent content={course.description || 'Explore this course.'} />
                        </div>
                      </div>
                      <div className="browse-course-card-footer">
                        <span>{course.lesson_count || 0} lesson{(course.lesson_count || 0) === 1 ? '' : 's'}</span>
                        <span className="browse-course-arrow">View course <span aria-hidden="true">→</span></span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
      </main>
      <LearningFooter />
    </div>
  );
}
